import {
  RouteConnectionModeEnum,
  RouteFieldTesterSuggestionDTO,
  RouteGapSuggestionsDTO,
  RoutePackageDTO,
  RoutePlanTbLevelEnum,
} from 'api/model';
// Type-only: selection-helpers importerar bestGapChoice härifrån, och en värdeimport åt båda
// håll hade gett en runtime-cykel. Typer raderas vid kompilering och är därför ofarliga.
import type { GapChoice, PackageSelection } from './selection-helpers';

/**
 * Paketets TB med de VALDA lucka-åtgärderna inräknade.
 *
 * Backend räknar `package.CostSek` som summan av benens schablonkostnad — persontransporten,
 * utfyllnaden och annonsen ligger utanför, eftersom de väljs av trafikledaren efter att förslaget
 * är byggt. Följden var att kortet kunde skriva "God marginal" på ett paket vars valda åtgärder
 * kostade mer än benen: 1 816 kr i benkostnad mot 2 560 kr i persontransport. Och det är just de
 * kostnaderna som gör avlämningar i obygden dyra, så talet var tystast precis där det behövdes.
 *
 * Här justeras intäkt och kostnad efter valet, och klassen räknas om. Hotellnatten går inte att
 * prissätta (planeringen har ingen hotellkostnad) — den kapar i stället grönt till gult, av samma
 * skäl som schablonintäkt gör det: en känd kostnad som inte är kvantifierad får inte lova grönt.
 *
 * Se .claude/features/route-planning.md ("TB-språket i kortet").
 */

/** Buffertgräns för grönt när arbetstiden är uppskattad — samma som EstimatedDurationGreenFloor. */
const ESTIMATED_DURATION_GREEN_FLOOR = 0.2;

export interface TbLevelInput {
  revenueSek: number | null;
  costSek: number;
  /** Intäkten är schablon (300 kr/h) i stället för prislistepris. */
  revenueIsEstimated: boolean;
  /** Minst ett uppdrags arbetstid är uppskattad, inte bokad. */
  durationIsEstimated: boolean;
  /** En känd men oprissatt kostnad finns (hotellnatt) — får aldrig lova grönt. */
  hasUnpricedCost?: boolean;
}

/**
 * SPEGLAR `RoutePlanningLogic.ComputeTb`. Ändras trösklarna där måste de ändras här — testerna i
 * `tb-remedies.test.ts` jämför utfallet mot de nivåer backend skickar för ojusterade paket, så
 * avvikelsen syns.
 *
 * Klienten använder den ENDAST när ett val har ändrat talen. Utan valda åtgärder gäller backends
 * `TbLevel` och `TbBufferPercent` rakt av.
 */
export const computeTbLevel = ({
  revenueSek,
  costSek,
  revenueIsEstimated,
  durationIsEstimated,
  hasUnpricedCost,
}: TbLevelInput): RoutePlanTbLevelEnum => {
  if (revenueSek == null || revenueSek <= 0) {
    return RoutePlanTbLevelEnum.Unknown;
  }

  const buffer = (revenueSek - costSek) / revenueSek;
  const level =
    buffer > 0.1
      ? RoutePlanTbLevelEnum.Green
      : buffer >= 0
        ? RoutePlanTbLevelEnum.Yellow
        : RoutePlanTbLevelEnum.Red;

  if (
    level === RoutePlanTbLevelEnum.Green &&
    (revenueIsEstimated ||
      hasUnpricedCost === true ||
      (durationIsEstimated && buffer < ESTIMATED_DURATION_GREEN_FLOOR))
  ) {
    return RoutePlanTbLevelEnum.Yellow;
  }

  return level;
};

export const tbBufferPercent = (
  revenueSek: number | null,
  costSek: number
): number | null => {
  if (revenueSek == null || revenueSek <= 0) return null;
  return Math.round(((revenueSek - costSek) / revenueSek) * 1000) / 10;
};

interface RemedyEconomy {
  revenueSek: number | null;
  costSek: number;
}

/**
 * Vad en vald åtgärd gör med paketets ekonomi. `revenueSek: null` = åtgärden har ingen prisrad, och
 * då är paketets TB okänt snarare än gissat — samma regel som ett ben utan pris redan följer.
 */
const remedyEconomy = (
  choice: GapChoice,
  gap: RouteGapSuggestionsDTO | undefined
): RemedyEconomy | undefined => {
  if (!gap) return undefined;

  switch (choice) {
    case 'personalTransport':
      // EstimatedCostSek är negativt (= kostnad) i DTO:n. Ren kostnad, ingen intäkt.
      return gap.personalTransport
        ? { revenueSek: 0, costSek: -gap.personalTransport.estimatedCostSek }
        : undefined;
    case 'assignmentRequest':
      return gap.assignmentRequest
        ? {
            revenueSek: gap.assignmentRequest.revenueSek ?? null,
            costSek: gap.assignmentRequest.costSek,
          }
        : undefined;
    case 'filler':
      return gap.filler
        ? {
            revenueSek: gap.filler.topCandidateRevenueSek ?? null,
            costSek: gap.filler.topCandidateCostSek,
          }
        : undefined;
    case 'rideAlong':
      // Samåkningen själv kostar inget — bilen körs ändå. Men åker hon kollektivt till bilen
      // (hem) eller från avstigningen (ut) skapas en persontransport, och den kostar. Samma
      // villkor som buildConnectionActions: bara en anslutning som faktiskt räknas blir en
      // persontransport. En underkänd resa bär också Transit, för att kunna visas.
      return gap.rideAlong?.connection?.mode === RouteConnectionModeEnum.Transit &&
        gap.rideAlong.connection.transit
        ? { revenueSek: 0, costSek: -gap.rideAlong.connection.transit.estimatedCostSek }
        : undefined;
    default:
      return undefined;
  }
};

export interface AdjustedTb {
  /** Intäkt inklusive valda åtgärder. Null = någon vald åtgärd saknar prisrad. */
  revenueSek: number | null;
  /** Kostnad inklusive valda åtgärder. */
  costSek: number;
  bufferPercent: number | null;
  level: RoutePlanTbLevelEnum;
  /** Åtgärdernas kostnadsdel, för hoverns egen rad. 0 = inget valt. */
  remedyCostSek: number;
  /** Åtgärdernas intäktsdel. */
  remedyRevenueSek: number;
  /** En vald åtgärd saknar prisrad — TB kan inte beräknas. */
  hasUnpricedRemedy: boolean;
  /** Hotellnatt krävs men går inte att prissätta i planeringen. */
  hotelUnpriced: boolean;
  /** True när talen skiljer sig från backends — dvs. någon åtgärd faktiskt räknats in. */
  adjusted: boolean;
}

/**
 * Åtgärden med högst täckningsbidrag, som förval i luckväljaren.
 *
 * Tidigare förvaldes alltid "Gör inget", vilket i praktiken betydde att fälttestaren fick ta sig
 * hem på egen hand om ingen orkade öppna väljaren — även när ett annonsben hade gett +643 kr.
 *
 * Rangordningen använder samma <c>remedyEconomy</c> som TB-hovern, så förvalet och siffran
 * planeraren ser inte kan säga emot varandra. En åtgärd utan prisrad har okänt TB och rankas
 * under varje prissatt alternativ — men över "gör inget", eftersom den fortfarande kan vara den
 * enda vägen hem.
 */
export const bestGapChoice = (
  gap: RouteGapSuggestionsDTO | undefined
): GapChoice => {
  if (!gap) return 'none';

  // Mellanlandning mot logistiken förväljs när backend rekommenderar den (TL-feedback
  // 2026-10-13: "endast tåg tillbaka i nödfall", och annonsbil ner från Strängnäs "endast i
  // undantagsfall"). Den går därför före både tåget och annonsen. Utåt förväljs den aldrig —
  // beslutet 2026-09-17 står kvar där: en bil på väg ut i landet är trafikledarens beslut.
  if (gap.hubStopover?.isRecommended && !gap.hubStopover.isOutbound) {
    return 'hubStopover';
  }

  // Övriga mellanlandningar förväljs inte: de är ingen intäktsåtgärd utan ett sätt att korta
  // dagen, och förslaget ska belysa vad vi tänker göra och varför.
  const ranked: GapChoice[] = ['assignmentRequest', 'filler', 'personalTransport'];
  let best: { choice: GapChoice; tbSek: number } | undefined;

  ranked.forEach((choice) => {
    // Under riktvärdet för lastbil förväljs annonsen inte (beslut 2026-09-24): den tar en plats
    // som en lastbil kunde ha fyllt. Den finns kvar att välja — riktvärdet är ingen spärr.
    if (choice === 'assignmentRequest' && gap.assignmentRequest?.isBelowTruckShareTarget) return;

    const economy = remedyEconomy(choice, gap);
    if (!economy) return;

    const tbSek =
      economy.revenueSek == null
        ? Number.NEGATIVE_INFINITY
        : economy.revenueSek - economy.costSek;

    if (!best || tbSek > best.tbSek) {
      best = { choice, tbSek };
    }
  });

  // Samåkningen rankas inte på TB — den har ingen intäkt och sparar bara en biljett — men är den
  // enda vägen hem ska den väljas. Paketet klassas "redo" på den (TL 2026-10-02), och ett förval
  // på "gör inget" hade lämnat fälttestaren kvar vid avlämningen. Gruppens utfyllnadsbil
  // förväljs inte: den finns bara om bärarens hemresa blir just den bilen.
  if (!best && gap.rideAlong && !gap.rideAlong.carrierIsFiller) return 'rideAlong';

  return best?.choice ?? 'none';
};

export const tbWithRemedies = ({
  pkg,
  candidate,
  selection,
  needsHotel,
}: {
  pkg: RoutePackageDTO;
  candidate: RouteFieldTesterSuggestionDTO | undefined;
  selection: PackageSelection;
  needsHotel: boolean;
}): AdjustedTb => {
  const chosen = [
    remedyEconomy(selection.firstGapChoice, candidate?.firstLegGap),
    remedyEconomy(selection.lastGapChoice, candidate?.lastLegGap),
    // Överföringarnas val räknas med. De är FÖRVALDA och skapar riktiga åtgärder vid verkställ,
    // så utan dem visade badgen ett TB som inte var det som faktiskt tillämpades.
    ...pkg.legs.map((leg) =>
      leg.transferGap
        ? remedyEconomy(
            selection.transferGapChoices?.[leg.assignmentId] ?? 'none',
            leg.transferGap
          )
        : undefined
    ),
  ].filter((r): r is RemedyEconomy => r !== undefined);

  const remedyCostSek = chosen.reduce((sum, r) => sum + r.costSek, 0);
  const remedyRevenueSek = chosen.reduce((sum, r) => sum + (r.revenueSek ?? 0), 0);
  const hasUnpricedRemedy = chosen.some((r) => r.revenueSek == null);

  const durationIsEstimated = pkg.legs.some((l) => l.durationIsEstimated);
  const adjusted = chosen.length > 0;

  const revenueSek =
    hasUnpricedRemedy || pkg.revenueSek == null
      ? null
      : pkg.revenueSek + remedyRevenueSek;
  const costSek = pkg.costSek + remedyCostSek;

  // Utan valda åtgärder är backends klassning den enda sanningen — klienten räknar inte om något
  // den inte har ändrat. Hotellnatten är undantaget: den kapar grönt även när inget är valt.
  if (!adjusted && !needsHotel) {
    return {
      revenueSek: pkg.revenueSek ?? null,
      costSek: pkg.costSek,
      bufferPercent: pkg.tbBufferPercent ?? null,
      level: pkg.tbLevel,
      remedyCostSek: 0,
      remedyRevenueSek: 0,
      hasUnpricedRemedy: false,
      hotelUnpriced: false,
      adjusted: false,
    };
  }

  return {
    revenueSek,
    costSek,
    bufferPercent: tbBufferPercent(revenueSek, costSek),
    level: computeTbLevel({
      revenueSek,
      costSek,
      revenueIsEstimated: pkg.tbIsEstimated,
      durationIsEstimated,
      hasUnpricedCost: needsHotel,
    }),
    remedyCostSek,
    remedyRevenueSek,
    hasUnpricedRemedy,
    hotelUnpriced: needsHotel,
    adjusted,
  };
};
